<script lang="ts" setup>
import InputLabel from "@/components/Reusables/Inputs/InputLabel.vue";
import SearchSelectWithKeyValue from "@/components/Reusables/Inputs/SearchSelectWithKeyValue.vue";
import SelectInputNew from "@/components/Reusables/Inputs/SelectInputNew.vue";
import LoadingLogoSpinner from "@/components/Reusables/Loading/LoadingLogoSpinner.vue";
import { Country_Enum, GetBuildingPartServiceLifetimesQuery, useGetBuildingPartServiceLifetimesQuery } from "@/graphql";
import GetLevelsLifetimesQuery from "@/graphql/queries/GetLevelsLifetimesQuery";
import { useQuery } from "@vue/apollo-composable";
import { useModalStore } from "@/stores/modals/modals.store";
import { computed, ref, watch } from "vue";
import MaterialServiceLifetimeTable from "./MaterialServiceLifetimeTable.vue";
import MaterialServiceLifetimeTableLevels from "./MaterialServiceLifetimeTableLevels.vue";
import LifetimeTable from "@/components/Views/MaterialMapping/Modals/AssignLifeTimeModal/LifetimeTable/LifetimeTable.vue";
import { MaterialServiceLifetimeSelectorMaterialRow, MaterialServiceLifetimeSelectorParentGroup } from "./types";
import {
  BBSR_LIFETIME_SOURCE_ID,
  GetLevelsLifetimesQueryResult,
  GetLevelsLifetimesQueryVariables,
  LEVELS_LIFETIME_SOURCE_ID,
  LevelsTableElementRow,
  LevelsTableParentGroup,
} from "./LevelsLifetimesTypes";
import { countryInAlpha2Map } from "@/providers/Localization/localizationConfig";
import { useAuthStateStore } from "@/stores/Utils/auth/AuthState";
import { useProject } from "@/stores/project/project.store";
import { storeToRefs } from "pinia";

type Item = GetBuildingPartServiceLifetimesQuery["buildingPartLifetimeSource"][0]["serviceLifetimes"][0];

const props = defineProps<{
  country?: Country_Enum;
  classificationCode?: string;
  materialCategoryId?: string;
}>();

// localStorage key for the user's lifetime-table country choice, scoped per user so different
// users on the same machine don't overwrite each other's choice.
const { user } = storeToRefs(useAuthStateStore());
const libraryCountryStorageKey = computed(() => `gaiup-${user.value?.id ?? "anonymous"}-library-lifetime-country`);

// True when opened without a fixed project country (Library context) — renders a country flag
// picker in the header and persists the selection to localStorage.
const showCountryPicker = computed(() => !props.country);

// Countries with no country-specific lifetime source all rely on the shared EU Level(s) table,
// grouped under a single "EU" flag rather than five buttons that do the same thing.
const EU_GROUPED_COUNTRIES: readonly Country_Enum[] = [
  Country_Enum.ITALY,
  Country_Enum.SPAIN,
  Country_Enum.FRANCE,
  Country_Enum.NETHERLANDS,
  Country_Enum.UNITED_KINGDOM,
];

// Virtual selection key for the grouped EU option.
const EU_GROUP_KEY = "EU";
type SelectableCountry = Country_Enum | typeof EU_GROUP_KEY;

// The EU group's query needs a real country_enum value; any EU_GROUPED_COUNTRIES member returns the
// same data, so the first one is used as the canonical query input.
const QUERY_COUNTRY_FOR_EU: Country_Enum = EU_GROUPED_COUNTRIES[0];

const readStoredCountry = (): SelectableCountry | null => {
  try {
    const stored = localStorage.getItem(libraryCountryStorageKey.value);
    if (!stored) return null;
    if (stored === EU_GROUP_KEY) return EU_GROUP_KEY;
    return (Object.values(Country_Enum) as string[]).includes(stored) ? (stored as Country_Enum) : null;
  } catch {
    // localStorage unavailable (e.g. private mode) — fall back to default
    return null;
  }
};

// Resolve the country used by the underlying queries. In project contexts
// `props.country` is the source of truth and `selectedCountry` is ignored.
const selectedCountry = ref<SelectableCountry>(props.country ?? readStoredCountry() ?? Country_Enum.DENMARK);

const activeCountry = computed<Country_Enum>(() => {
  if (props.country) return props.country;
  if (selectedCountry.value === EU_GROUP_KEY) return QUERY_COUNTRY_FOR_EU;
  return selectedCountry.value;
});

const selectCountry = (country: SelectableCountry) => {
  selectedCountry.value = country;
  if (showCountryPicker.value) {
    try {
      localStorage.setItem(libraryCountryStorageKey.value, country);
    } catch {
      // best-effort: a write failure should not break the picker
    }
  }
};

// Country flag chips for the header. Real EU member entries are hidden — they're represented by the
// single synthetic EU chip instead.
const countryFlagOptions = computed(() => {
  const flagFor = (alpha2: string) =>
    alpha2
      .toUpperCase()
      .split("")
      .map((char) => String.fromCodePoint(0x1f1e6 - 65 + char.charCodeAt(0)))
      .join("");

  const niceLabel = (value: string) =>
    value
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/(^\w|\s\w)/g, (match) => match.toUpperCase());

  const options: { value: SelectableCountry; flag: string; label: string }[] = [];
  for (const value of Object.values(Country_Enum) as Country_Enum[]) {
    if (EU_GROUPED_COUNTRIES.includes(value)) continue;
    const alpha2 = countryInAlpha2Map[value] ?? "";
    options.push({ value, flag: alpha2 ? flagFor(alpha2) : "", label: niceLabel(value) });
  }
  // The regional-indicator "EU" sequence renders as the EU flag where supported, falling back to
  // two-letter glyphs elsewhere — same as every other flag emoji here.
  options.push({ value: EU_GROUP_KEY, flag: flagFor("eu"), label: "EU" });
  return options;
});

// Denmark has no classification-code source in the picker's GraphQL data — it uses the legacy
// `LifetimeTable` component (REST-fed) in place of the picker instead.
const useDanishLegacyTable = computed(() => activeCountry.value === Country_Enum.DENMARK);

const projectStore = useProject();
const { lifetimeTable: projectLifetimeTable } = storeToRefs(projectStore);
const modals = useModalStore();

// Loads the legacy Danish table lazily, regardless of whether the modal opened from a project or
// the Library — in Library context the store's `lifetimeTable` is typically null until fetched.
watch(
  useDanishLegacyTable,
  async (active) => {
    if (active && !projectLifetimeTable.value) {
      try {
        await projectStore.fetchLifetimeTable();
      } catch {
        // best-effort: failure to load is rendered by the legacy table itself
      }
    }
  },
  { immediate: true }
);

// The Danish legacy table is much wider than the other tables, so the default narrow wrapper
// (`max-w-2xl`) cuts it off. Expand it while Denmark is active; restore it otherwise.
const ownModal = computed(() => modals.stack.find((entry) => entry.name === "MaterialServiceLifetimeSelectorModal") ?? null);
const originalWrapperClass = ref<string | null>(null);
const EXPANDED_WRAPPER_CLASS = "max-w-[95vw] h-[90dvh]";

watch(
  useDanishLegacyTable,
  (active) => {
    const entry = ownModal.value;
    if (!entry) return;
    if (originalWrapperClass.value === null) {
      originalWrapperClass.value = entry.wrapperClass ?? "";
    }
    entry.wrapperClass = active ? EXPANDED_WRAPPER_CLASS : originalWrapperClass.value;
  },
  { immediate: true }
);

const handleLegacyDanishSelect = (payload: { value: number }) => {
  modals.closeByName("MaterialServiceLifetimeSelectorModal", { lifetime: payload.value });
};

// The EU Level(s) reference table is exposed as a data source in every country, alongside any
// country-specific classification-code sources; the dropdown picks between them.
const { loading: classificationLoading, result: classificationResult } = useGetBuildingPartServiceLifetimesQuery(() => ({
  country: activeCountry.value,
}));

const { loading: levelsLoading, result: levelsResult } = useQuery<
  GetLevelsLifetimesQueryResult,
  GetLevelsLifetimesQueryVariables
>(GetLevelsLifetimesQuery, { sourceId: LEVELS_LIFETIME_SOURCE_ID });

const loading = computed(() => classificationLoading.value || levelsLoading.value);
const result = computed(() => classificationResult.value);

const handleSelect = (lifetime: number) => {
  modals.closeByName("MaterialServiceLifetimeSelectorModal", { lifetime });
};

const dataSourceId = ref<string>("");

const dataSource = computed(() => {
  return result.value?.buildingPartLifetimeSource.find((source) => source.id === dataSourceId.value);
});

const levelsSource = computed(() => levelsResult.value?.buildingPartLifetimeSource?.[0] ?? null);

const search = ref("");

// Whether the currently selected data source is the EU Level(s) reference table.
// Drives which renderer (classification-code vs. Level(s)) is used.
const useLevelsTable = computed(() => dataSourceId.value === LEVELS_LIFETIME_SOURCE_ID);

// The German (BBSR) source shares the classification-code renderer with the Norwegian source, but
// child-level classification badges are suppressed only for the German variant.
const isBbsrSource = computed(() => dataSourceId.value === BBSR_LIFETIME_SOURCE_ID);

const dataSources = computed(() => {
  const classificationSources = result.value?.buildingPartLifetimeSource ?? [];
  const levels = levelsSource.value ? [levelsSource.value] : [];
  // Show country-specific classification-code sources first, then the EU Level(s) source.
  return [...classificationSources, ...levels];
});

const selectedSource = computed(() => {
  if (useLevelsTable.value) return levelsSource.value;
  return dataSource.value ?? null;
});

const materialOptions = computed(() => {
  const lifetimes = dataSource.value?.serviceLifetimes ?? [];

  // Deduplicate by label, but keep `isFavorite: true` if ANY row with that label is suggested.
  const byName = new Map<string, { name: string; value: string; isFavorite: boolean | null }>();

  for (const x of lifetimes) {
    const name = x.buildingElementClassificationCodeMapping?.buildingElement?.label || "";
    if (!name) continue;

    const suggested = isSuggested(x);
    const existing = byName.get(name);

    if (!existing) {
      byName.set(name, {
        name,
        value: name,
        isFavorite: suggested ? true : null,
      });
    } else if (suggested && existing.isFavorite !== true) {
      existing.isFavorite = true;
    }
  }

  return Array.from(byName.values()).sort((a, b) => {
    if (a.isFavorite === b.isFavorite) return 0;
    if (a.isFavorite === true) return -1;
    if (b.isFavorite === true) return 1;
    return 0;
  });
});

const isSuggested = (item: Item) => {
  const mapping = item.buildingElementClassificationCodeMapping;
  if (!mapping) return false;

  if (mapping.classificationCode.code === props.classificationCode) {
    if (!mapping.buildingElement) {
      return true;
    }

    return !!mapping.buildingElement?.materialCategoryMappings.find((x) => x.materialCategory.id === props.materialCategoryId);
  }

  return false;
};

const toNumberOrNull = (v: unknown): number | null => {
  if (v == null) return null;
  if (typeof v === "number" && Number.isFinite(v)) return v;

  // In case your backend ever stores excel strings directly
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (s === "ikke relevant") return null;
    if (s.includes("mer enn 50")) return null;
    const n = Number(s.replace(",", "."));
    return Number.isFinite(n) ? n : null;
  }

  return null;
};

const usableItems = computed(() => {
  return (
    (dataSource.value?.serviceLifetimes ?? [])
      .map((item) => {
        const high = toNumberOrNull((item as any).lifetimeHigh);
        const avg = toNumberOrNull((item as any).lifetimeAverage);
        const low = toNumberOrNull((item as any).lifetimeLow);

        return { item, high, avg, low };
      })
      // ignore rows that have no selectable lifetime at all
      .filter((x) => x.high != null || x.avg != null || x.low != null)
  );
});

const filteredUsableItems = computed(() => {
  if (!search.value) return usableItems.value;

  const s = search.value.toLowerCase();
  return usableItems.value.filter((x) => {
    const buildingPartName = x.item.buildingElementClassificationCodeMapping?.buildingElement?.label || "";
    return buildingPartName.toLowerCase().includes(s);
  });
});

const grouped = computed<MaterialServiceLifetimeSelectorParentGroup[]>(() => {
  const parentMap = new Map<string, MaterialServiceLifetimeSelectorParentGroup>();

  // Resolves the parent (header) code from `classificationCode.parent`, falling back to the row's own
  // code/label only when no parent is linked — keeps parentless rows usable as their own header.
  const getParentHeader = (
    mapping: NonNullable<
      GetBuildingPartServiceLifetimesQuery["buildingPartLifetimeSource"][0]["serviceLifetimes"][0]["buildingElementClassificationCodeMapping"]
    >
  ): { code: string; label: string } | null => {
    const parent = mapping.classificationCode.parent;
    if (parent?.code) {
      return {
        code: parent.code.toString().trim(),
        label: parent.label?.toString().trim() ?? "",
      };
    }
    const code = mapping.classificationCode.code?.toString().trim();
    if (!code) return null;
    return { code, label: mapping.classificationCode.label?.toString().trim() ?? "" };
  };

  // Pre-seed parent buckets so that headers render in a stable order even if the first row
  // arriving for a parent comes from `filteredUsableItems` (which may be filtered down).
  for (const item of dataSource.value?.serviceLifetimes ?? []) {
    const mapping = item.buildingElementClassificationCodeMapping;
    if (!mapping) continue;

    const header = getParentHeader(mapping);
    if (!header) continue;

    if (!parentMap.has(header.code)) {
      parentMap.set(header.code, { code: header.code, label: header.label, children: [] });
    }
  }

  // Add children/material rows only from usable items (selectable rows)
  for (const x of filteredUsableItems.value) {
    const mapping = x.item.buildingElementClassificationCodeMapping;
    if (!mapping) continue;

    const code = mapping.classificationCode.code?.toString().trim();
    const label = mapping.classificationCode.label?.toString().trim() ?? "";
    if (!code) continue;

    const header = getParentHeader(mapping);
    if (!header) continue;

    // Skip rows where the row itself is the parent header (no grandparent linked).
    if (header.code === code) continue;

    const isUncategorized = !mapping.buildingElement;

    let parent = parentMap.get(header.code);
    if (!parent) {
      parent = { code: header.code, label: header.label, children: [] };
      parentMap.set(header.code, parent);
    }

    let child = parent.children.find((c) => c.code === code);
    if (!child) {
      child = { code, label, rows: [] };
      parent.children.push(child);
    }

    child.rows.push({
      id: x.item.id,
      item: x.item,
      materialLabel: mapping.buildingElement?.label ?? "",
      high: x.high,
      avg: x.avg,
      low: x.low,
      isSuggested: isSuggested(x.item),
      isUncategorized,
    });
  }

  // Sort by parent (2-digit) and child (3-digit) classification code.
  // Within each child group, keep suggested rows first (stable within each bucket).
  const sortCode = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true });

  return Array.from(parentMap.values())
    .map((p) => ({
      ...p,
      children: [...p.children]
        .sort((a, b) => sortCode(a.code, b.code))
        .map((c) => {
          const suggested: MaterialServiceLifetimeSelectorMaterialRow[] = [];
          const other: MaterialServiceLifetimeSelectorMaterialRow[] = [];

          for (const r of c.rows) {
            (r.isSuggested ? suggested : other).push(r);
          }

          return {
            ...c,
            rows: [...suggested, ...other],
          };
        }),
    }))
    .filter((p) => p.children.length > 0)
    .sort((a, b) => sortCode(a.code, b.code));
});

// ---- Level(s) grouping ----

const levelsBuildingPartLookup = computed(() => {
  const map = new Map<string, string>();
  for (const part of levelsResult.value?.buildingPart ?? []) {
    map.set(part.id, part.label);
  }
  return map;
});

const levelsBuildingElementLookup = computed(() => {
  const map = new Map<string, string>();
  for (const element of levelsResult.value?.building_element ?? []) {
    map.set(element.id, element.label);
  }
  return map;
});

const levelsMaterialOptions = computed(() => {
  if (!useLevelsTable.value) return [];

  const byName = new Map<string, { name: string; value: string; isFavorite: boolean | null }>();
  for (const row of levelsSource.value?.serviceLifetimes ?? []) {
    if (!row.buildingElementId) continue;
    const name = levelsBuildingElementLookup.value.get(row.buildingElementId) ?? "";
    if (!name) continue;
    if (!byName.has(name)) {
      byName.set(name, { name, value: name, isFavorite: null });
    }
  }
  return Array.from(byName.values()).sort((a, b) => a.name.localeCompare(b.name));
});

const groupedLevels = computed<LevelsTableParentGroup[]>(() => {
  if (!useLevelsTable.value) return [];

  const source = levelsSource.value;
  if (!source) return [];

  const partLookup = levelsBuildingPartLookup.value;
  const elementLookup = levelsBuildingElementLookup.value;

  const searchTerm = search.value.trim().toLowerCase();

  // Group: parentId → buildingPartId → element rows
  const parents = new Map<string, LevelsTableParentGroup>();

  for (const row of source.serviceLifetimes) {
    const part = row.buildingPart;
    if (!part) continue;

    const elementLabel = row.buildingElementId ? elementLookup.get(row.buildingElementId) ?? "" : "";
    if (!elementLabel) continue;

    // Search by element label.
    if (searchTerm && !elementLabel.toLowerCase().includes(searchTerm)) continue;

    // Skip rows with no usable lifetime value.
    if (row.lifetimeLow == null && row.lifetimeAverage == null && row.lifetimeHigh == null) continue;

    // Resolve parent group. If the building part has no parent, group under
    // itself so it still renders as a top-level section.
    const parentId = part.parent_id ?? part.id;
    const parentLabel = part.parent_id ? partLookup.get(part.parent_id) ?? part.label : part.label;

    let parent = parents.get(parentId);
    if (!parent) {
      parent = { id: parentId, label: parentLabel, children: [] };
      parents.set(parentId, parent);
    }

    let child = parent.children.find((c) => c.id === part.id);
    if (!child) {
      child = { id: part.id, label: part.label, rows: [] };
      parent.children.push(child);
    }

    const elementRow: LevelsTableElementRow = {
      id: row.id,
      elementLabel,
      low: row.lifetimeLow,
      avg: row.lifetimeAverage,
      high: row.lifetimeHigh,
    };
    child.rows.push(elementRow);
  }

  // Sort everything alphabetically for a stable, browsable layout.
  return Array.from(parents.values())
    .map((parent) => ({
      ...parent,
      children: [...parent.children]
        .sort((a, b) => a.label.localeCompare(b.label))
        .map((child) => ({
          ...child,
          rows: [...child.rows].sort((a, b) => a.elementLabel.localeCompare(b.elementLabel)),
        })),
    }))
    .filter((parent) => parent.children.length > 0)
    .sort((a, b) => a.label.localeCompare(b.label));
});

const visibleMaterialOptions = computed(() => (useLevelsTable.value ? levelsMaterialOptions.value : materialOptions.value));

const hasResults = computed(() => (useLevelsTable.value ? groupedLevels.value.length > 0 : grouped.value.length > 0));

watch(
  dataSources,
  (sources) => {
    // Defaults to the first source once data arrives, and resets the selection when the active list
    // no longer contains it (e.g. after a country switch).
    if (sources.length === 0) {
      dataSourceId.value = "";
      return;
    }
    const stillExists = sources.some((source) => source.id === dataSourceId.value);
    if (!stillExists) {
      dataSourceId.value = sources[0].id;
    }
  },
  { immediate: true }
);
</script>
<template>
  <div class="flex h-full w-full flex-col">
    <header class="z-dropdown bg-slate-0 sticky top-0">
      <div class="border-b border-slate-200 px-6">
        <h4 class="t-h4 text-type-default pb-2">Lifetime Reference Table</h4>

        <p class="text-type-muted mt-1 py-2 text-xs">
          Select a Low, Average, or High lifetime for the relevant building part and material. The selected value will be inserted
          into the Lifetime field.
        </p>
        <!-- Library country picker: shown when no project country is fixed. -->
        <div
          v-if="showCountryPicker"
          class="t-xs mt-2 pb-2">
          <InputLabel label="Country" />
          <p class="t-xs text-type-muted mt-1">Choose a country to get the specific lifetime table for you country</p>
          <div
            class="mt-1 flex flex-wrap gap-1"
            role="radiogroup"
            aria-label="Select country for lifetime table">
            <button
              v-for="option in countryFlagOptions"
              :key="option.value"
              v-tooltip="{ text: option.label, variant: 'label', placement: 'top' }"
              type="button"
              role="radio"
              :aria-checked="selectedCountry === option.value"
              :aria-label="option.label"
              :class="[
                'focus-ring duration-fast ease-standard flex h-10 items-center justify-center rounded-full border px-3 transition-colors',
                selectedCountry === option.value
                  ? 'border-app-blue-600 bg-app-blue-50 text-app-blue-700'
                  : 'text-type-muted bg-slate-0 hover:text-type-default border-slate-200 hover:border-slate-300 hover:bg-slate-100',
              ]"
              @click="selectCountry(option.value)">
              <span class="t-body leading-none">{{ option.flag }}</span>
            </button>
          </div>
        </div>
        <div
          v-if="!useLevelsTable && !useDanishLegacyTable"
          class="mt-2 pb-4 text-xs">
          <div class="text-type-muted flex items-center gap-2">
            <font-awesome-icon
              class="text-app-yellow-400"
              :icon="'fa-solid fa-star'" />
            <span>Starred rows are suggested based on the selected building part and material.</span>
          </div>
        </div>
      </div>
      <!-- Table filter toolbar -->
      <div
        v-if="!useDanishLegacyTable"
        class="z-dropdown relative px-3 py-3">
        <div class="flex items-center justify-between gap-4">
          <div class="w-full flex-col">
            <InputLabel label="Material / Product" />
            <SearchSelectWithKeyValue
              v-model="search"
              :options="visibleMaterialOptions"
              :disabled="false"
              placeholder="Search" />
          </div>
          <div class="flex w-full flex-col">
            <div class="mb-1 flex items-center gap-2">
              <InputLabel label="Data Source" />

              <a
                v-if="selectedSource?.source"
                :href="selectedSource.source"
                target="_blank"
                rel="noopener noreferrer"
                class="bg-slate-0 text-app-blue-600 inline-flex w-fit items-center gap-2 border-slate-300 text-xs"
                @click.stop>
                <font-awesome-icon
                  icon="fa-solid fa-arrow-up-right-from-square"
                  class="t-caption" />
              </a>
            </div>

            <SelectInputNew v-model="dataSourceId">
              <option
                disabled
                value="">
                Select data source
              </option>
              <option
                v-for="source in dataSources"
                :key="source.id"
                :value="source.id">
                {{ source.label }}
              </option>
            </SelectInputNew>
          </div>
        </div>
      </div>
    </header>

    <div
      v-if="loading && !useDanishLegacyTable"
      class="flex h-[calc(100vh-72px)] w-full items-center justify-center">
      <LoadingLogoSpinner
        size="md"
        fill="true" />
    </div>

    <div
      v-else-if="useDanishLegacyTable"
      class="min-h-0 w-full grow overflow-auto">
      <div class="h-full px-2">
        <LifetimeTable
          :focus-construction-id-alternate="''"
          @close="handleLegacyDanishSelect" />
      </div>
    </div>

    <div
      v-else
      class="min-h-0 w-full grow">
      <div class="h-full px-2">
        <div
          v-if="!hasResults"
          class="text-type-muted flex w-full items-center justify-center py-10 text-sm">
          No results were found.
        </div>
        <MaterialServiceLifetimeTableLevels
          v-else-if="useLevelsTable"
          :items="groupedLevels"
          @select="handleSelect" />

        <MaterialServiceLifetimeTable
          v-else
          :items="grouped"
          :hide-child-classification="isBbsrSource"
          @select="handleSelect" />
      </div>
    </div>
  </div>
</template>
